import * as vp from 'vopay';
import { useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { usePartnerAccounts } from '@/providers/PartnerAccounts.provider';
import { useInvoiceDetails } from '@/providers/InvoiceDetails.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';

import useFileDownload from '@/hooks/useFiledDownload';
import InvoicingForm from './InvoicingForm';

import type { PartnerInvoiceDetailsRequestParams } from '@/hooks/useApi/useFetchPartnerInvoiceDetails';
import { useTranslation } from 'react-i18next';

const { Box, Button, Icon, SubTitle, Table, Title } = vp;

const InvoicingAccount = (): React.JSX.Element => {
  const { downloadJson } = useFileDownload();
  const { accountId } = useParams<{ accountId: string }>();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const { response: partnerAccounts, refreshPartnerAccounts } = usePartnerAccounts();
  const { invoiceDetails, invoicePeriod, refreshInvoiceDetails } = useInvoiceDetails();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.InvoicingAccount' });

  const invoiceDetailValues = getInvoiceDetails();
  const initValues = parseInvoicePeriod();

  useEffect(() => {
    refreshPartnerAccounts({
      VopayAccountID: accountId,
    });
  }, [accountId]);

  const search = (values: PartnerInvoiceDetailsRequestParams) => {
    refreshInvoiceDetails({ vopayAccountId: accountId, year: values.year, month: values.month });
  };

  return (
    <Box flx="col">
      <Title title={getAccountName()} />
      <Box pt="sm" pb="sm" jc="sb">
        <InvoicingForm initValues={initValues} search={search} />
        <Button
          id="invoicing-details-account-json-download"
          size="sm"
          label={t('downloadJSONButton')}
          icon="download"
          iconLocation="right"
          iconSize="md"
          isDisabled={isRoleViewOnly}
          onClick={() => downloadJson(getDownloadData(), getDownloadFileName())}
        />
      </Box>
      {hasInvoiceDetails() ? (
        buildDetailTables()
      ) : (
        <Box pt="lg" pb="sm" gap="sm">
          <SubTitle subTitle={t('noInvoiceDetails')} />
        </Box>
      )}
    </Box>
  );

  function buildDetailTables() {
    return Object.entries(invoiceDetailValues).map(([currency, services]) =>
      Object.entries(services).map(([service, values]) => {
        const isRatePercentage = isQuantityFloat();

        const tableProps: vp.TableProps = {
          name: `${accountId}-${currency}-${service}`,
          variant: 'pair',
          columns: {
            key: { header: 'Key' },
            value: { header: 'value' },
          },
          rows: [
            {
              key: 'Rate',
              value: isRatePercentage ? formatAsPercentage(String(values.rate)) : formatAsCurrency(String(values.rate)),
            },
            {
              key: isRatePercentage ? 'Total Sum of Transactions' : 'Number of Transactions',
              value: isRatePercentage
                ? formatAsCurrency(String(values.quantity))
                : formatAsQuantity(String(values.quantity)),
            },
            { key: 'Total Amount', value: formatAsCurrency(String(values.amount)) },
          ],
        };

        return (
          <Box key={`${accountId}-${currency}-${service}`} mb="lg" flx="col">
            <Box pt="lg" flx="col">
              <strong>{`${values.description} | ${currency}`}</strong>
              <Table {...tableProps} />
            </Box>
          </Box>
        );

        function isQuantityFloat() {
          /*
            The rate can be represented as either a dollar amount or a percentage.
            Because this is not explicit, we have to rely on whether or not the quantity is representing the number of  transactions (no decimal),
            or is representing a dollar amount (has a decimal).
              
            If the quantity is a dollar amount, the rate will be a percentage of this dollar amount.
          */

          return String(values.quantity).split('.').length > 1;
        }
      }),
    );
  }

  function hasInvoiceDetails() {
    const servicesByCurrency = Object.values(invoiceDetailValues);

    return servicesByCurrency.length === 0
      ? false
      : servicesByCurrency.some((services) =>
          Object.values(services).some((serviceDetails) => Object.values(serviceDetails).length > 0),
        );
  }

  function formatAsCurrency(str: string) {
    return (
      <Box ai="center">
        <Icon icon="dollar" size="sm" />
        {parseFloat(str.replace(/,/g, '')).toFixed(2)}
      </Box>
    );
  }

  function formatAsPercentage(str: string) {
    return (
      <Box ai="center">
        <Icon icon="percentage" size="sm" />
        {parseFloat(str.replace(/,/g, '')).toFixed(2)}
      </Box>
    );
  }

  function formatAsQuantity(str: string) {
    return (
      <Box ai="center">
        <Icon icon="multiply" size="xsm" />
        {Math.round(parseFloat(str.replace(/,/g, '')))}
      </Box>
    );
  }

  function getDownloadData() {
    return { invoicePeriod, invoiceDetails: { [accountId]: invoiceDetails?.[accountId] || {} } };
  }

  function getDownloadFileName() {
    return `invoice-details-${accountId}-${initValues.month}-${initValues.year}`;
  }

  function getInvoiceDetails() {
    return invoiceDetails?.[accountId] || {};
  }

  function getAccountName() {
    return partnerAccounts?.accountDetails[accountId].accountName || '';
  }

  function parseInvoicePeriod() {
    const fromDate = invoicePeriod?.FromDate || '';
    const [year, month] = fromDate.split('-');

    return { year: year || '', month: month || '' };
  }
};

export default InvoicingAccount;
